import { useState, useRef, useMemo } from 'react';
import { T } from '../theme';
import { parseBankStatementCSV, parseCSVWithAIFormat, markDuplicates, detectCreditCardFormat, scoreFilenameAgainstAccounts } from '../utils/csvParser';
import { detectCSVFormatAI } from '../utils/csvFormatApi';
import { isXLSX, xlsxToCSV } from '../utils/xlsxParser';
import { Upload, X, CheckCircle, AlertCircle, Landmark, ChevronDown } from 'lucide-react';

const TAG_STYLE = {
  fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
  display: 'inline-block', marginLeft: 4,
};

const NEW_ACCOUNT = '__new__';

export default function BankImportModal({ onClose, onImport, existingFiles = [], registeredAccounts = [] }) {
  const [dragOver, setDragOver] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [newAccountName, setNewAccountName] = useState('');
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [keepLocal, setKeepLocal] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [fileName, setFileName] = useState('');
  const [xlsxConverted, setXlsxConverted] = useState(false);
  const fileInputRef = useRef();

  // Unique existing account names for this report type (file-derived + explicitly registered)
  const existingAccounts = useMemo(() => {
    const seen = new Set();
    const accounts = [];
    for (const f of existingFiles) {
      const name = f.accountName || f.name;
      if (!seen.has(name)) { seen.add(name); accounts.push({ name, updatedAt: f.updatedAt }); }
    }
    for (const a of registeredAccounts) {
      if (!seen.has(a.name)) { seen.add(a.name); accounts.push({ name: a.name }); }
    }
    return accounts;
  }, [existingFiles, registeredAccounts]);

  const effectiveAccount = selectedAccount === NEW_ACCOUNT ? newAccountName.trim() : selectedAccount;

  // Transactions from the selected account (for duplicate checking)
  const accountExisting = useMemo(() => {
    if (!effectiveAccount) return [];
    return existingFiles
      .filter(f => (f.accountName || f.name) === effectiveAccount)
      .flatMap(f => f.data ?? []);
  }, [existingFiles, effectiveAccount]);

  // Rank existing accounts by filename similarity; null topMatch means no signal
  const topAccountMatch = useMemo(() => {
    if (!fileName || existingAccounts.length === 0) return null;
    const scores = scoreFilenameAgainstAccounts(fileName, existingAccounts);
    return scores[0]?.score > 0 ? scores[0].name : null;
  }, [fileName, existingAccounts]);

  // Re-mark duplicates whenever account changes
  const markedTxs = useMemo(() => {
    if (!parsed) return [];
    return markDuplicates(parsed.transactions, accountExisting);
  }, [parsed, accountExisting]);

  const filteredTxs = skipDuplicates ? markedTxs.filter(tx => !tx.isDuplicate) : markedTxs;
  const debits = filteredTxs.filter(tx => tx.transactionType === 'debit');
  const credits = filteredTxs.filter(tx => tx.transactionType === 'credit');
  const dupCount = markedTxs.filter(tx => tx.isDuplicate).length;
  const totalDebits = debits.reduce((s, tx) => s + parseFloat(tx.amount || 0), 0);
  const totalCredits = credits.reduce((s, tx) => s + parseFloat(tx.amount || 0), 0);

  const handleFile = async (file) => {
    if (!file) return;
    setParseError(null);
    const xlsx = isXLSX(file);
    let text;
    try {
      text = xlsx ? await xlsxToCSV(file) : await file.text();
    } catch { setParseError('Could not read file.'); return; }

    // Wrong-type check: if the CC detector recognises the headers, refuse early
    const firstLine = text.split(/\r?\n/).find(l => l.trim());
    if (firstLine) {
      const headers = firstLine.split(',').map(h => h.replace(/^["']|["']$/g, '').trim());
      if (detectCreditCardFormat(headers)) {
        setParseError('This looks like a credit card statement. Please use Import Credit Card Statement instead.');
        return;
      }
    }

    let result = parseBankStatementCSV(text);

    if (!result.format) {
      setIsDetecting(true);
      try {
        const aiCols = await detectCSVFormatAI(text, 'bank');
        if (aiCols.fileType === 'credit_card') {
          setParseError('This looks like a credit card statement. Please use Import Credit Card Statement instead.');
          return;
        }
        result = parseCSVWithAIFormat(text, aiCols, 'bank');
      } catch (err) {
        console.warn('[detectCSVFormat] AI detection failed:', err?.message);
      } finally {
        setIsDetecting(false);
      }
    }

    if (!result.format) {
      setParseError('Could not detect a supported bank statement format. Please check the file and try again.');
      return;
    }
    setParsed(result);
    setFileName(file.name);
    setXlsxConverted(xlsx);
    // Force explicit account selection — no auto-fill
    if (existingAccounts.length === 0) {
      setSelectedAccount(NEW_ACCOUNT);
      setNewAccountName('');
    } else {
      setSelectedAccount('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const canImport = effectiveAccount && filteredTxs.length > 0 && confirmed;

  const handleImport = () => {
    if (!canImport) return;
    onImport({ accountName: effectiveAccount, transactions: filteredTxs, reportType: 'bank', keepLocal });
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16,
        width: '100%', maxWidth: 580,
        maxHeight: '90vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{
          background: T.gradNavy, padding: '16px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Landmark size={20} color="#fff" />
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>Import Bank Statement</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: 20, flex: 1 }}>
          {/* Upload zone (always visible until parsed) */}
          {!parsed && (
            <>
              {parseError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 16, color: '#dc2626', fontSize: 13 }}>
                  {parseError}
                </div>
              )}
              {isDetecting ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6B7280', fontSize: 13 }}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Analyzing format with AI…</div>
                  <div style={{ color: '#9CA3AF', fontSize: 12 }}>Unrecognized format — asking AI to identify columns</div>
                </div>
              ) : (
                <>
                  <div
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: `2px dashed ${dragOver ? T.tealDeep : '#D1D5DB'}`,
                      borderRadius: 12, padding: '40px 20px',
                      textAlign: 'center', cursor: 'pointer',
                      background: dragOver ? '#f0fdfa' : '#fafafa',
                      transition: 'all 0.15s',
                    }}
                  >
                    <Upload size={32} color={T.tealDeep} style={{ marginBottom: 12 }} />
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Drop your CSV or Excel file here</div>
                    <div style={{ color: '#6B7280', fontSize: 13 }}>or click to browse</div>
                    <div style={{ color: '#9CA3AF', fontSize: 11, marginTop: 12 }}>
                      Supports: Chase Bank, Bank of America, Wells Fargo · CSV, XLSX, XLS
                    </div>
                  </div>
                  <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,.ods" style={{ display: 'none' }}
                    onChange={e => handleFile(e.target.files[0])} />
                </>
              )}
            </>
          )}

          {/* Preview + account picker */}
          {parsed && (
            <>
              {/* Format badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <CheckCircle size={16} color="#16a34a" />
                <span style={{ fontSize: 13, color: '#374151' }}>
                  Detected: <strong>{xlsxConverted ? `Excel · ${parsed.format}` : parsed.format}</strong>
                </span>
                <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 'auto' }}>
                  {parsed.transactions.length} rows
                  {parsed.skipped > 0 && `, ${parsed.skipped} skipped`}
                </span>
                <button
                  onClick={() => { setParsed(null); setParseError(null); setSelectedAccount(''); setNewAccountName(''); setConfirmed(false); setFileName(''); setXlsxConverted(false); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: 12, padding: 0 }}
                >
                  Change file
                </button>
              </div>


              {/* Account picker */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Bank account</div>
                <div style={{ position: 'relative' }}>
                  <select
                    value={selectedAccount}
                    onChange={e => { setSelectedAccount(e.target.value); setConfirmed(false); }}
                    style={{
                      width: '100%', padding: '9px 32px 9px 12px', borderRadius: 8,
                      border: `1.5px solid ${selectedAccount === '' ? '#f59e0b' : '#D1D5DB'}`,
                      fontSize: 14, appearance: 'none',
                      background: '#fff', cursor: 'pointer', fontWeight: 600,
                    }}
                  >
                    {selectedAccount === '' && (
                      <option value="" disabled>Select account…</option>
                    )}
                    {existingAccounts.map(a => (
                      <option key={a.name} value={a.name}>{a.name}</option>
                    ))}
                    <option value={NEW_ACCOUNT}>+ New account…</option>
                  </select>
                  <ChevronDown size={14} color="#6B7280" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                </div>
                {selectedAccount === NEW_ACCOUNT && (
                  <input
                    type="text"
                    value={newAccountName}
                    onChange={e => { setNewAccountName(e.target.value); setConfirmed(false); }}
                    placeholder="e.g. Chase Checking"
                    autoFocus
                    style={{
                      marginTop: 8, width: '100%', padding: '9px 12px', borderRadius: 8,
                      border: '1.5px solid #D1D5DB', fontSize: 14, boxSizing: 'border-box',
                    }}
                  />
                )}
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  Transactions are grouped by account. Duplicates are checked within the same account.
                </div>
              </div>

              {/* Options */}
              <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
                  <input type="checkbox" checked={skipDuplicates} onChange={e => setSkipDuplicates(e.target.checked)} />
                  <span style={{ fontSize: 13 }}>
                    Skip duplicates
                    {dupCount > 0 && <span style={{ color: '#92400e', marginLeft: 4 }}>({dupCount} found in {effectiveAccount || 'this account'})</span>}
                    {dupCount === 0 && effectiveAccount && <span style={{ color: '#6B7280', marginLeft: 4 }}>(none found)</span>}
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={keepLocal} onChange={e => setKeepLocal(e.target.checked)} />
                  <span style={{ fontSize: 13 }}>Keep local — don't sync to cloud</span>
                </label>
              </div>

              {/* Summary */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <div style={{ flex: 1, padding: '10px', background: '#FEF2F2', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#dc2626' }}>{debits.length}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>Debits (expenses)</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>${totalDebits.toFixed(2)}</div>
                </div>
                <div style={{ flex: 1, padding: '10px', background: '#F0FDF4', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#16a34a' }}>{credits.length}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>Credits (income)</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>${totalCredits.toFixed(2)}</div>
                </div>
              </div>

              {/* Transaction list */}
              <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, marginBottom: 16 }}>
                {filteredTxs.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6B7280', fontSize: 13 }}>
                    {dupCount > 0 && skipDuplicates ? 'All transactions already exist in this account.' : 'No transactions to import.'}
                  </div>
                ) : filteredTxs.map(tx => (
                  <div key={tx.id} style={{
                    padding: '8px 12px', borderBottom: '1px solid #F3F4F6',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: tx.transactionType === 'credit' ? '#16a34a' : '#dc2626',
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {tx.vendor}
                        {tx.businessClassification === 'transfer' && (
                          <span style={{ ...TAG_STYLE, background: '#ede9fe', color: '#6d28d9' }}>TRANSFER</span>
                        )}
                        {tx.isDuplicate && (
                          <span style={{ ...TAG_STYLE, background: '#fef3c7', color: '#92400e' }}>DUP</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{tx.date}</div>
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: 700, flexShrink: 0,
                      color: tx.transactionType === 'credit' ? '#16a34a' : '#111',
                    }}>
                      {tx.transactionType === 'credit' ? '+' : ''}${parseFloat(tx.amount).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {!effectiveAccount && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, color: '#dc2626', fontSize: 13 }}>
                  <AlertCircle size={14} /> Please enter an account name.
                </div>
              )}

              {effectiveAccount && (() => {
                const hasSignal = topAccountMatch !== null && selectedAccount !== NEW_ACCOUNT;
                const isGood = !hasSignal || effectiveAccount === topAccountMatch;
                const bg     = isGood ? (confirmed ? '#f0fdf4' : '#fefce8') : '#fef2f2';
                const border = isGood ? (confirmed ? '#86efac' : '#fde047') : '#fca5a5';
                return (
                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                    background: bg, border: `1.5px solid ${border}`,
                    borderRadius: 8, padding: '10px 12px', marginBottom: 12,
                  }}>
                    <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
                      style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                      I've reviewed the transactions from <strong>{fileName}</strong> and confirm
                      they are correct and belong to <strong>{effectiveAccount || '…'}</strong>.
                      {isGood && confirmed && <span style={{ color: '#16a34a', marginLeft: 6 }}>✓</span>}
                      {!isGood && (
                        <span style={{ display: 'block', marginTop: 4, color: '#dc2626', fontSize: 12 }}>
                          This file looks like it may belong to <strong>{topAccountMatch}</strong> — please confirm you have the right account.
                        </span>
                      )}
                    </span>
                  </label>
                );
              })()}

              <button
                onClick={handleImport}
                disabled={!canImport}
                style={{
                  width: '100%', padding: '12px',
                  borderRadius: 10, border: 'none',
                  background: canImport ? T.gradTeal : '#E5E7EB',
                  color: canImport ? '#fff' : '#9CA3AF',
                  fontSize: 14, fontWeight: 700,
                  cursor: canImport ? 'pointer' : 'not-allowed',
                }}
              >
                Import {filteredTxs.length} transaction{filteredTxs.length !== 1 ? 's' : ''} into {effectiveAccount || '…'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
